/**
 * A player's progress in one game (ADR 0029), as the browser keeps it and the api stores it for a
 * signed-in person. Every field only grows, so two copies merge field by field with the best
 * winning: in any order, any number of times, nothing is ever lost.
 */
export interface Progress {
  /** Missions won, by id, with the best stars (0 for a mission that gives none). */
  won: Record<string, number>;
  /** The campaign run's best: how many missions cleared. */
  bestRun: number;
  /** A game's own counters (e.g. a quiz's words answered right per category); optional. */
  marks?: Record<string, number>;
}

/** At most this many marks per game, and this long a name. */
export const MAX_MARKS = 200;
const ID = /^[a-z0-9_-]{1,32}$/;
const MAX_STARS = 3;
const MAX_COUNT = 1_000_000_000;

export const emptyProgress = (): Progress => ({ won: {}, bestRun: 0 });

const whole = (v: unknown, max: number): number | null =>
  typeof v === "number" && Number.isInteger(v) && v >= 0 ? Math.min(v, max) : null;

/**
 * A record from storage or the network, cleaned: unknown missions dropped (when the game's
 * mission ids are given), stars clamped to 0-3, whole numbers only, marks capped. Never throws.
 */
export function cleanProgress(raw: unknown, missionIds?: readonly string[]): Progress {
  const r = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const known = missionIds ? new Set(missionIds) : null;
  const won: Record<string, number> = {};
  if (typeof r["won"] === "object" && r["won"] !== null)
    for (const [id, stars] of Object.entries(r["won"] as Record<string, unknown>)) {
      const s = whole(stars, MAX_STARS);
      if (s !== null && ID.test(id) && (known === null || known.has(id))) won[id] = s;
    }
  const out: Progress = { won, bestRun: whole(r["bestRun"], MAX_COUNT) ?? 0 };
  if (typeof r["marks"] === "object" && r["marks"] !== null) {
    const marks: Record<string, number> = {};
    for (const [name, value] of Object.entries(r["marks"] as Record<string, unknown>)) {
      if (Object.keys(marks).length >= MAX_MARKS) break;
      const v = whole(value, MAX_COUNT);
      if (v !== null && ID.test(name)) marks[name] = v;
    }
    if (Object.keys(marks).length > 0) out.marks = marks;
  }
  return out;
}

const maxOf = (a: Record<string, number>, b: Record<string, number>) => {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = Math.max(out[k] ?? 0, v);
  return out;
};

/** The two copies together: every win, the best stars, the best run, the higher of each mark. */
export function mergeProgress(a: Progress, b: Progress): Progress {
  const out: Progress = { won: maxOf(a.won, b.won), bestRun: Math.max(a.bestRun, b.bestRun) };
  if (a.marks || b.marks) out.marks = maxOf(a.marks ?? {}, b.marks ?? {});
  return out;
}

/** True if `b` has anything `a` lacks: a win, more stars, a better run or a higher mark. */
export function addsTo(a: Progress, b: Progress): boolean {
  const more = (x: Record<string, number> = {}, y: Record<string, number> = {}) =>
    Object.entries(y).some(([k, v]) => x[k] === undefined || v > (x[k] ?? 0));
  return b.bestRun > a.bestRun || more(a.won, b.won) || more(a.marks, b.marks);
}
